import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Copy, Trash2, Search, Star, Zap, TrendingDown, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatPrice, typeLabel } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import Seo from "../../components/Seo";

const statusColors = {
  active: "bg-indigo-100 text-indigo-800", sold: "bg-slate-200 text-slate-700",
  hold: "bg-amber-100 text-amber-800", inactive: "bg-slate-100 text-slate-500",
};

export default function AdminProperties() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { data } = useQuery({ queryKey: ["admin-props", q, status], queryFn: async () => (await api.get("/admin/properties", { params: { q: q || undefined, status: status || undefined } })).data });

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["admin-props"] }); } catch { toast.error("Action failed"); } };
  const del = (p) => { if (window.confirm(`Delete ${p.property_id}?`)) act(() => api.delete(`/admin/properties/${p.id}`), "Deleted"); };
  const setStatusVal = (p, s) => act(() => api.patch(`/admin/properties/${p.id}/status`, { status: s }), `Marked ${s}`);
  const toggleFlag = (p, flag) => act(() => api.patch(`/admin/properties/${p.id}/status`, { [flag]: !p[flag] }), "Updated");

  return (
    <div>
      <Seo title="Property Management | Admin" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Properties</h1><p className="text-slate-500">Manage your listings.</p></div>
        <Link to="/admin/properties/new"><Button className="bg-indigo-900 hover:bg-indigo-800" data-testid="add-property-btn"><Plus className="w-4 h-4 mr-2" /> Add Property</Button></Link>
      </div>

      <div className="flex items-center gap-3 mt-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or ID…" data-testid="admin-search-props" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
        </div>
        <div className="flex gap-1">
          {["", "active", "sold", "hold", "inactive"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-3 py-2 rounded-lg text-sm capitalize ${status === s ? "bg-indigo-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{s || "All"}</button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-props-table">
          <thead><tr className="border-b border-slate-100 text-left text-slate-500">
            <th className="p-4">Property</th><th className="p-4">ID</th><th className="p-4">Price</th><th className="p-4">Status</th><th className="p-4">Badges</th><th className="p-4">Views</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {(data?.items || []).map((p) => (
              <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="p-4"><div className="font-medium text-slate-900 line-clamp-1">{p.title}</div><div className="text-xs text-slate-400">{typeLabel(p.property_type)} · {p.sector}</div></td>
                <td className="p-4 font-mono text-xs text-slate-500">{p.property_id}</td>
                <td className="p-4 font-semibold text-slate-800">{formatPrice(p.price)}</td>
                <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${statusColors[p.status]}`}>{p.status}</span></td>
                <td className="p-4"><div className="flex gap-1">{p.featured && <Star className="w-4 h-4 text-amber-500" />}{p.urgent_sale && <Zap className="w-4 h-4 text-rose-500" />}{p.price_reduced && <TrendingDown className="w-4 h-4 text-indigo-600" />}</div></td>
                <td className="p-4 text-slate-500">{p.views || 0}</td>
                <td className="p-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button data-testid={`prop-actions-${p.property_id}`} className="p-1.5 hover:bg-slate-100 rounded-lg"><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => nav(`/admin/properties/${p.id}/edit`)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => act(() => api.post(`/admin/properties/${p.id}/duplicate`), "Duplicated")}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleFlag(p, "featured")}><Star className="w-4 h-4 mr-2" /> Toggle Featured</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusVal(p, p.status === "active" ? "inactive" : "active")}>{p.status === "active" ? "Unpublish" : "Publish"}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusVal(p, "sold")}>Mark Sold</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => del(p)} className="text-rose-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data?.items?.length && <div className="p-12 text-center text-slate-400">No properties. Add your first listing.</div>}
      </div>
    </div>
  );
}
